import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  mutationErrorMessage,
  mutationErrorTone,
  mutationSuccessMessage,
  type MutationToastMeta,
} from '@/lib/api/mutation-toast';

import { useToast } from './use-toast';

/**
 * Announces the outcome of **every** mutation — every POST / PUT / PATCH / DELETE — as a toast.
 *
 * Mounted once, above the router. Watching the mutation cache rather than adding `onSuccess` /
 * `onError` at each `useMutation` is the point: a new mutation is covered the moment it is written,
 * and cannot ship silent because somebody forgot the handler.
 *
 * The rules, in one place:
 * - **2xx on any non-GET → green success toast.** Wording comes from `meta.successMessage`
 *   (a string, or a function of the mutation's own variables); a mutation that declares none still
 *   announces itself with generic copy rather than saying nothing.
 * - **Failure → amber warning toast, except a `400`, which is red.** See `mutationErrorTone`.
 *
 * A mutation opts out of the failure half with `meta.suppressErrorToast` (it presents the error
 * itself) or out of both with `meta.suppressToast`. Renders nothing.
 */
export function MutationToaster() {
  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type !== 'updated') return;

        const { action } = event;
        if (action.type !== 'success' && action.type !== 'error') return;

        const meta = event.mutation.meta as MutationToastMeta | undefined;
        if (meta?.suppressToast) return;

        if (action.type === 'success') {
          toast.success({
            title: mutationSuccessMessage(meta, event.mutation.state.variables),
          });
          return;
        }

        if (meta?.suppressErrorToast) return;

        const title = meta?.errorMessage ?? mutationErrorMessage(action.error);
        if (mutationErrorTone(action.error) === 'error') {
          toast.error({ title });
          return;
        }
        toast.warning({ title });
      }),
    [queryClient, toast],
  );

  return null;
}
